/**
 * Certification Detail Tab (Shared between Admin and Process Owner)
 * 
 * Primary workspace to review certification materials
 * Features:
 * - Read-only Attestation Statement (PO) / Editable (Admin)
 * - 6 Review Cards with message functionality
 * - Acknowledge Certification flow with modal (PO only)
 * 
 * Usage:
 * - Admin Tab 2: View/edit certification for selected Process Owner
 * - PO Tab 1: View/submit certification for self
 */

import React, { useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Modal, Checkbox, message, Dropdown, Input, Tooltip } from 'antd';
import { CheckCircleFilled, ExclamationCircleOutlined, SearchOutlined, RightOutlined, LinkOutlined, LoadingOutlined } from '@ant-design/icons';
import MessageSquareIcon from '../../../../../assets/icons/ic-message-square.svg?react';
import styled from 'styled-components';
import { RootState, AppDispatch } from '../../../../../store';
import {
  markProcessesCertified,
} from '../../../../../store/slices/certificationSlice';
import {
  submitCertification,
  addCertificationComment,
  updateAttestationContent,
  type Quarter,
  type ProcessOwner,
  type CertificationCommentType,
} from '../../../../../services/certificationService';
import { downloadFile } from '../../../../../services/fileService';
import ChevronDownIcon from '../../../../../assets/icons/ic-chevron-down.svg?react';
import FileBadgeIcon from '../../../../../assets/icons/ic-file-badge.svg?react';
import CommentDrawer from './CommentDrawer';

// ========================================
// Props Interface
// ========================================

interface CertificationDetailTabProps {
  selectedQuarter: Quarter;
  onQuarterChange: (quarter: Quarter) => void;
  // Admin-specific props
  isAdmin: boolean;
  selectedProcessOwner?: ProcessOwner | null;
  processOwners?: ProcessOwner[];
  onProcessOwnerChange?: (ownerId: string) => void;
  // Loading state from parent
  isLoading?: boolean;
}

// ========================================
// Styled Components
// ========================================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const HeaderControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// Quarter Chip (dropdown trigger)
const QuarterChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  height: 38px;
  background: #F0FDF4;
  color: #166534;
  border-radius: 18px;
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.005em;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    opacity: 0.8;
  }
`;

const QuarterChevronIcon = styled.div`
  display: flex;
  align-items: center;
  color: #166534;
  
  svg {
    width: 14px;
    height: 14px;
  }
  
  svg path {
    stroke: #166534;
  }
`;

const QuarterDropdownMenu = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  padding: 8px;
  min-width: 120px;
`;

const QuarterDropdownItem = styled.div<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$selected ? '#F0FDF4' : 'white'};
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: ${props => props.$selected ? '#166534' : '#1F2937'};

  &:hover {
    background: ${props => props.$selected ? '#F0FDF4' : '#F3F4F6'};
  }
`;

// Top row with Cycles + Process Owner
const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
`;

// Cycles/Processes row
const ChipsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ChipLabel = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #6B7280;
  min-width: 70px;
`;

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

// Cycle chip - Green style (from FinancialStatementItemDrawer)
const CycleChip = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  background: #ECFDF5;
  border: 1px solid #A7F3D0;
  border-radius: 16px;
  font-family: Inter;
  font-weight: 500;
  font-size: 13px;
  color: #115E59;
  cursor: default;
`;

// Process chip - Blue style
const ProcessChip = styled.div<{ $isCertified: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  background: ${({ $isCertified }) => $isCertified ? '#F3F4F6' : '#EFF6FF'};
  border: 1px solid ${({ $isCertified }) => $isCertified ? '#D1D5DB' : '#BFDBFE'};
  border-radius: 16px;
  font-family: Inter;
  font-weight: 500;
  font-size: 13px;
  color: ${({ $isCertified }) => $isCertified ? '#6B7280' : '#1E40AF'};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${({ $isCertified }) => $isCertified ? '#E5E7EB' : '#DBEAFE'};
  }
`;

// Process Owner Chip (from RiskAssessmentDetailPage style)
const ProcessOwnerChip = styled.div<{ $clickable?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  height: 38px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 18px;
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.005em;
  cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
  transition: all 0.2s;
  
  ${({ $clickable }) => $clickable && `
    &:hover {
      opacity: 0.8;
    }
  `}
`;

const ChevronIcon = styled.div`
  display: flex;
  align-items: center;
  color: #1E40AF;
  
  svg {
    width: 14px;
    height: 14px;
  }
  
  svg path {
    stroke: #1E40AF;
  }
`;

// Dropdown styles
const DropdownMenu = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  padding: 8px;
  width: 280px;
  max-height: 320px;
  overflow-y: auto;
`;

const SearchInput = styled(Input)`
  margin-bottom: 8px;
  border-radius: 6px;
  height: 38px;
  
  .ant-input {
    font-family: Inter;
    font-size: 14px;
  }
`;

const DropdownItem = styled.div<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$selected ? '#EFF6FF' : 'white'};

  &:hover {
    background: ${props => props.$selected ? '#EFF6FF' : '#F3F4F6'};
  }
`;

const DropdownItemAvatar = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #EFF6FF;
  border-radius: 50%;
  font-family: Inter;
  font-weight: 600;
  font-size: 12px;
  color: #1E40AF;
  flex-shrink: 0;
`;

const DropdownItemText = styled.span<{ $selected?: boolean }>`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: ${props => props.$selected ? '#2563EB' : '#1F2937'};
  flex: 1;
`;

const AttestationBlurb = styled.div`
  background: #D1FAE5;
  border: 1px solid #A7F3D0;
  border-radius: 12px;
  padding: 20px;
  position: relative;
`;

const AttestationHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const CheckIcon = styled.div`
  color: #059669;
  font-size: 24px;
`;

const AttestationContent = styled.div`
  flex: 1;
`;

const AttestationTitle = styled.div<{ $editable?: boolean }>`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  color: #065F46;
  margin-bottom: 8px;
  ${({ $editable }) => $editable && `
    cursor: text;
    &:hover {
      background: rgba(255, 255, 255, 0.5);
      border-radius: 4px;
    }
  `}
`;

const AttestationDescription = styled.div<{ $editable?: boolean }>`
  font-family: Inter;
  font-size: 14px;
  color: #047857;
  line-height: 1.6;
  ${({ $editable }) => $editable && `
    cursor: text;
    &:hover {
      background: rgba(255, 255, 255, 0.5);
      border-radius: 4px;
    }
  `}
`;

const ReviewCard = styled.div`
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px;
`;

const CardTitleSection = styled.div`
  flex: 1;
`;

const CardTitle = styled.div<{ $editable?: boolean }>`
  font-family: Inter;
  font-weight: 600;
  font-size: 15px;
  color: #1F2937;
  margin-bottom: 4px;
  
  ${({ $editable }) => $editable && `
    cursor: text;
    padding: 4px 8px;
    margin: -4px -8px;
    margin-bottom: 0;
    border-radius: 4px;
    border: 1px solid transparent;
    transition: all 0.2s;
    
    &:hover {
      border-color: #D1D5DB;
      background: rgba(0, 0, 0, 0.02);
    }
    
    &:focus {
      outline: none;
      border-color: #2563EB;
      background: white;
    }
  `}
`;

const CardDescription = styled.div<{ $editable?: boolean }>`
  font-family: Inter;
  font-size: 13px;
  color: #6B7280;
  line-height: 1.5;
  
  ${({ $editable }) => $editable && `
    cursor: text;
    padding: 4px 8px;
    margin: -4px -8px;
    margin-top: 4px;
    border-radius: 4px;
    border: 1px solid transparent;
    transition: all 0.2s;
    
    &:hover {
      border-color: #D1D5DB;
      background: rgba(0, 0, 0, 0.02);
    }
    
    &:focus {
      outline: none;
      border-color: #2563EB;
      background: white;
    }
  `}
`;

const MessageButtonWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MessageButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  background: #F8FAFC;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #EFF6FF;
    
    svg path {
      stroke: #2563EB;
    }
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ContentChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ControlGroupTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
`;

const ControlGroupLabel = styled.span`
  font-weight: 600;
  margin-right: 4px;
`;

const ControlNumberCircle = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  background: transparent;
  color: #2563EB;
  border: 1px solid #D1D5DB;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: #DBEAFE;
    border-color: #93C5FD;
  }
`;

// Content chip - Blue style with link icon (same as ProcessChip but with icon)
const ContentChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #EFF6FF;
  border: 1px solid #BFDBFE;
  border-radius: 16px;
  font-family: Inter;
  font-weight: 500;
  font-size: 13px;
  color: #1E40AF;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #DBEAFE;
  }
  
  .link-icon {
    color: #2563EB;
    font-size: 12px;
  }
`;

// Content chip with download functionality and loading state
const ContentChipWithDownload: React.FC<{
  fileId: string;
  fileName: string;
  showIcon?: boolean;
}> = ({ fileId, fileName, showIcon = true }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isDownloading) return;
    
    setIsDownloading(true);
    try {
      await downloadFile(fileId, fileName);
    } catch (error) {
      console.error('Failed to download file:', error);
      message.error('Failed to download file');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <ContentChip onClick={handleDownload}>
      {showIcon && (
        isDownloading ? (
          <LoadingOutlined className="link-icon" spin />
        ) : (
          <LinkOutlined className="link-icon" />
        )
      )}
      {fileName}
    </ContentChip>
  );
};

// Gap chip - Red style for GAP items
const GapChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #FEE2E2;
  border: 1px solid #FECACA;
  border-radius: 16px;
  font-family: Inter;
  font-weight: 500;
  font-size: 13px;
  color: #991B1B;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #FECACA;
  }
`;

const ActionLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #2563EB;
  cursor: pointer;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
  
  .anticon {
    font-size: 12px;
  }
`;

const CardDivider = styled.div`
  height: 1px;
  background: #E5E7EB;
`;

const CardContent = styled.div`
  padding: 20px;
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  min-height: 400px;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #E5E7EB;
    border-top-color: #2563EB;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .text {
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    color: #6B7280;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const EmptyStateWrapper = styled.div`
  text-align: center;
  padding: 122px 20px;
  background: white;
  border-radius: 6px;
`;

const EmptyStateText = styled.div`
  color: #1F2937;
  font-family: Inter;
  font-size: 15px;
  font-weight: 500;
`;

// Acknowledge Certification Button
const AcknowledgeButton = styled(Button)`
  height: 46px;
  padding: 0 24px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  background: #2563EB;
  border-color: #2563EB;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  
  &:hover:not(:disabled) {
    background: #1D4ED8 !important;
    border-color: #1D4ED8 !important;
  }
  
  &:disabled {
    background: #9CA3AF !important;
    border-color: #9CA3AF !important;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

// Modal styled components
const CertificationModal = styled(Modal)`
  .ant-modal-header {
    text-align: center;
    border-bottom: none;
    padding: 24px 24px 0;
  }
  
  .ant-modal-title {
    font-family: Inter;
    font-weight: 600;
    font-size: 18px;
    color: #1F2937;
  }
  
  .ant-modal-body {
    padding: 16px 24px 24px;
  }
  
  .ant-modal-footer {
    border-top: none;
    padding: 0 24px 24px;
    display: flex;
    gap: 12px;
    
    .ant-btn {
      flex: 1;
      height: 44px;
      font-family: Inter;
      font-weight: 500;
      font-size: 14px;
      border-radius: 8px;
    }
    
    .ant-btn-default {
      border: 1px solid #2563EB;
      color: #2563EB;
      
      &:hover {
        border-color: #1D4ED8;
        color: #1D4ED8;
      }
    }
    
    .ant-btn-primary {
      background: #2563EB;
      border-color: #2563EB;
      
      &:hover:not(:disabled) {
        background: #1D4ED8;
        border-color: #1D4ED8;
      }
      
      &:disabled {
        background: #9CA3AF;
        border-color: #9CA3AF;
      }
    }
  }
`;

const ModalDescription = styled.p`
  font-family: Inter;
  font-size: 14px;
  color: #6B7280;
  text-align: center;
  margin-bottom: 20px;
  line-height: 1.5;
`;

const ModalWarning = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #DBEAFE;
  border: 1px solid #BFDBFE;
  border-radius: 8px;
  margin-bottom: 20px;
  
  .icon {
    color: #1D4ED8;
    font-size: 18px;
    flex-shrink: 0;
  }
  
  .text {
    font-family: Inter;
    font-size: 14px;
    color: #1D4ED8;
    line-height: 1.5;
  }
`;

const ProcessList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ProcessItem = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #F9FAFB;
  border-radius: 8px;
  cursor: pointer;
  
  &:hover {
    background: #F3F4F6;
  }
`;

const ProcessItemText = styled.span`
  font-family: Inter;
  font-size: 14px;
  color: #1F2937;
`;

// ========================================
// Component
// ========================================

const CertificationDetailTab: React.FC<CertificationDetailTabProps> = ({
  selectedQuarter,
  onQuarterChange,
  isAdmin,
  selectedProcessOwner,
  processOwners = [],
  onProcessOwnerChange,
  isLoading = false,
}) => {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const dispatch = useDispatch<AppDispatch>();
  
  // quarterData and checklistItems are now fetched by parent (CertificationPage) and stored in Redux
  const { quarterData, checklistItems } = useSelector((state: RootState) => state.certification);
  
  // Check if all checklist questions have been answered (have a significantChange status)
  // This determines if PO can acknowledge certification (P3.1 requirement)
  const isChecklistCompleted = checklistItems.length > 0 && 
    checklistItems.every(item => item.significantChange !== null && item.significantChange !== undefined);
  
  const [certifyModalVisible, setCertifyModalVisible] = useState(false);
  const [selectedProcessIds, setSelectedProcessIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [commentDrawerOpen, setCommentDrawerOpen] = useState(false);
  const [commentType, setCommentType] = useState<CertificationCommentType | null>(null);
  const [ownerSearchTerm, setOwnerSearchTerm] = useState('');
  const [ownerDropdownOpen, setOwnerDropdownOpen] = useState(false);
  const [quarterDropdownOpen, setQuarterDropdownOpen] = useState(false);
  
  // Get all processes from cycles
  const allProcesses = quarterData?.cycles.flatMap(c => c.processes) || [];
  
  // Filter process owners for search
  const filteredOwners = ownerSearchTerm
    ? processOwners.filter(po => 
        po.processOwnerName.toLowerCase().includes(ownerSearchTerm.toLowerCase())
      )
    : processOwners;
  
  // Handle process chip click
  const handleProcessClick = useCallback((process: any, openRiskAssessment: boolean) => {
    if (openRiskAssessment) {
      navigate(`/risk-assessment/${process.riskAssessmentId}/tab2`);
    } else {
      navigate('/process-hierarchy', { state: { selectedProcessId: process.processId } });
    }
  }, [navigate]);
  
  // Handle attestation save (Admin only)
  const handleAttestationSave = useCallback(async (field: string, value: string) => {
    if (!quarterData || !isAdmin) return;
    
    // Build request with updated field value
    const request = {
      certificationQuarterId: quarterData.certificationQuarterId,
      processOwnerMessage: field === 'processOwnerMessage' ? value : quarterData.attestation.processOwnerMessage,
      riskAssessmentTitle: field === 'riskAssessmentTitle' ? value : quarterData.attestation.riskAssessmentTitle,
      riskAssessmentDescription: field === 'riskAssessmentDescription' ? value : quarterData.attestation.riskAssessmentDescription,
      businessKnowledgeTitle: field === 'businessKnowledgeTitle' ? value : quarterData.attestation.businessKnowledgeTitle,
      businessKnowledgeDescription: field === 'businessKnowledgeDescription' ? value : quarterData.attestation.businessKnowledgeDescription,
      flowchartsTitle: field === 'flowchartsTitle' ? value : quarterData.attestation.flowchartsTitle,
      flowchardsDescription: field === 'flowchartsDescription' ? value : quarterData.attestation.flowchartsDescription,
      walkthroughsTitle: field === 'walkthroughsTitle' ? value : quarterData.attestation.walkthroughsTitle,
      walkthroughsDescription: field === 'walkthroughsDescription' ? value : quarterData.attestation.walkthroughsDescription,
      testSheetTitle: field === 'testSheetTitle' ? value : quarterData.attestation.testSheetTitle,
      testSheetDescription: field === 'testSheetDescription' ? value : quarterData.attestation.testSheetDescription,
      gapsTitle: field === 'gapsTitle' ? value : quarterData.attestation.gapsTitle,
      gapsDescription: field === 'gapsDescription' ? value : quarterData.attestation.gapsDescription,
      checklistTitle: field === 'checklistTitle' ? value : quarterData.attestation.checklistTitle,
      checklistDescription: field === 'checklistDescription' ? value : quarterData.attestation.checklistDescription,
    };
    
    try {
      await updateAttestationContent(request);
      // message.success('Saved');
    } catch (error) {
      console.error('Error saving attestation:', error);
      message.error('Failed to save');
    }
  }, [quarterData, isAdmin]);
  
  // Handle comment submission (PO only - Admin sees comments in Exhibit 9)
  const handleCommentSubmit = useCallback(async (data: { comment: string; commentType: CertificationCommentType | null }) => {
    if (!quarterData || !data.commentType || !data.comment.trim()) return;
    
    try {
      await addCertificationComment({
        message: data.comment.trim(),
        type: data.commentType,
        certificationQuarterId: quarterData.certificationQuarterId,
      });
      message.success('Comment added');
      setCommentDrawerOpen(false);
      setCommentType(null);
    } catch (error) {
      console.error('Error adding comment:', error);
      message.error('Failed to add comment');
    }
  }, [quarterData]);
  
  // Open comment drawer
  const openCommentDrawer = (type: CertificationCommentType) => {
    setCommentType(type);
    setCommentDrawerOpen(true);
  };
  
  // Handle certification submission (PO only)
  const handleSubmitCertification = useCallback(async () => {
    if (!quarterData || selectedProcessIds.length === 0) return;
    
    setSubmitting(true);
    try {
      await submitCertification({
        certificationQuarterId: quarterData.certificationQuarterId,
        processIds: selectedProcessIds,
      });
      
      dispatch(markProcessesCertified(selectedProcessIds));
      message.success('Certification successfully submitted.');
      setCertifyModalVisible(false);
      setSelectedProcessIds([]);
      
      // Refresh the page data
      window.location.reload();
    } catch (error) {
      console.error('Error submitting certification:', error);
      message.error('Failed to submit certification');
    } finally {
      setSubmitting(false);
    }
  }, [quarterData, selectedProcessIds, dispatch]);
  
  // Toggle process selection in modal
  const toggleProcessSelection = (processId: string) => {
    setSelectedProcessIds(prev => 
      prev.includes(processId)
        ? prev.filter(id => id !== processId)
        : [...prev, processId]
    );
  };

  // Toggle all process selection
  const toggleSelectAll = () => {
    const allIds = allProcesses.map(p => p.processId);
    setSelectedProcessIds(prev =>
      prev.length === allIds.length ? [] : allIds
    );
  };
  
  // Navigate to checklist tab
  const handleGoToChecklist = useCallback(() => {
    setSearchParams({ tab: 'checklist' }, { replace: true });
  }, [setSearchParams]);
  
  // Get owner initials for avatar
  const getOwnerInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };
  
  // Get display name for process owner
  // Get display name - always from selectedProcessOwner
  const getProcessOwnerDisplayName = () => {
    return selectedProcessOwner?.processOwnerName || '';
  };
  
  // Process Owner dropdown content
  const ownerDropdownContent = (
    <DropdownMenu onClick={(e) => e.stopPropagation()}>
      <SearchInput
        prefix={<SearchOutlined />}
        placeholder="Search process owner..."
        value={ownerSearchTerm}
        onChange={(e) => setOwnerSearchTerm(e.target.value)}
        autoFocus
      />
      {filteredOwners.map(po => {
        const isSelected = selectedProcessOwner?.id === po.id;
        return (
          <DropdownItem
            key={po.id}
            $selected={isSelected}
            onClick={() => {
              onProcessOwnerChange?.(po.id);
              setOwnerSearchTerm('');
              setOwnerDropdownOpen(false);
            }}
          >
            <DropdownItemAvatar>
              {getOwnerInitials(po.processOwnerName)}
            </DropdownItemAvatar>
            <DropdownItemText $selected={isSelected}>
              {po.processOwnerName}
            </DropdownItemText>
          </DropdownItem>
        );
      })}
      {filteredOwners.length === 0 && (
        <div style={{ padding: '16px', textAlign: 'center', color: '#9CA3AF', fontSize: 14 }}>
          No process owners found
        </div>
      )}
    </DropdownMenu>
  );
  
  const renderReviewCard = (
    title: string,
    description: string,
    type: CertificationCommentType,
    content: React.ReactNode,
    titleField?: string,
    descriptionField?: string,
  ) => (
    <ReviewCard>
      <CardHeader>
        <CardTitleSection>
          {isAdmin && titleField ? (
            <CardTitle
              $editable
              contentEditable
              onBlur={(e) => handleAttestationSave(titleField, e.currentTarget.textContent || '')}
              suppressContentEditableWarning
            >
              {title}
            </CardTitle>
          ) : (
            <CardTitle>{title}</CardTitle>
          )}
          {isAdmin && descriptionField ? (
            <CardDescription
              $editable
              contentEditable
              onBlur={(e) => handleAttestationSave(descriptionField, e.currentTarget.textContent || '')}
              suppressContentEditableWarning
            >
              {description}
            </CardDescription>
          ) : (
            <CardDescription>{description}</CardDescription>
          )}
        </CardTitleSection>
        {/* Message button with badge (PO only) */}
        {!isAdmin && (
          <MessageButtonWrapper>
            <MessageButton onClick={() => openCommentDrawer(type)}>
              <MessageSquareIcon />
            </MessageButton>
            {/* {(commentCount ?? 1) > 0 && (
              <MessageBadge>{commentCount ?? 1}</MessageBadge>
            )} */}
          </MessageButtonWrapper>
        )}
      </CardHeader>
      <CardDivider />
      <CardContent>
        {content}
      </CardContent>
    </ReviewCard>
  );
  
  // Show loading state when data is being fetched
  if (isLoading || !selectedProcessOwner) {
    return (
      <LoadingContainer>
        <div className="spinner" />
      </LoadingContainer>
    );
  }
  
  // Render header controls (used in both empty state and normal state)
  const renderHeaderControls = () => (
    <HeaderControls>
      {/* Top Row: Cycles + Process Owner */}
      <TopRow>
        {/* Cycles */}
        <ChipsRow>
          <ChipLabel>Cycles:</ChipLabel>
          <ChipsContainer>
            {quarterData?.cycles.map(c => (
              <CycleChip key={c.cycleId}>
                {c.cycleName}
              </CycleChip>
            )) || <span style={{ color: '#9CA3AF', fontSize: 13 }}>-</span>}
          </ChipsContainer>
        </ChipsRow>
        
        {/* Process Owner Chip - Admin with dropdown, PO read-only */}
        {isAdmin && selectedProcessOwner && onProcessOwnerChange ? (
          <Dropdown
            dropdownRender={() => ownerDropdownContent}
            trigger={['click']}
            placement="bottomRight"
            open={ownerDropdownOpen}
            onOpenChange={setOwnerDropdownOpen}
          >
            <ProcessOwnerChip $clickable>
              Process Owner: {getProcessOwnerDisplayName()}
              <ChevronIcon>
                <ChevronDownIcon />
              </ChevronIcon>
            </ProcessOwnerChip>
          </Dropdown>
        ) : (
          <ProcessOwnerChip>
            Process Owner: {getProcessOwnerDisplayName()}
          </ProcessOwnerChip>
        )}
      </TopRow>
      
      {/* Processes Row with Quarter Dropdown */}
      <TopRow>
        <ChipsRow style={{ flex: 1 }}>
          <ChipLabel>Processes:</ChipLabel>
          <ChipsContainer>
            {allProcesses.map(p => (
              <ProcessChip
                key={p.processId}
                $isCertified={p.isCertified}
                onClick={() => handleProcessClick(p, false)}
              >
                {p.processCode} {p.processName}
              </ProcessChip>
            ))}
            {allProcesses.length === 0 && (
              <span style={{ color: '#9CA3AF', fontSize: 13 }}>No processes</span>
            )}
          </ChipsContainer>
        </ChipsRow>
        
        {/* Quarter Dropdown */}
        <Dropdown
          dropdownRender={() => (
            <QuarterDropdownMenu>
              {([1, 2, 3, 4] as Quarter[]).map(q => (
                <QuarterDropdownItem
                  key={q}
                  $selected={selectedQuarter === q}
                  onClick={() => {
                    onQuarterChange(q);
                    setQuarterDropdownOpen(false);
                  }}
                >
                  Q{q}
                </QuarterDropdownItem>
              ))}
            </QuarterDropdownMenu>
          )}
          trigger={['click']}
          placement="bottomRight"
          open={quarterDropdownOpen}
          onOpenChange={setQuarterDropdownOpen}
        >
          <QuarterChip>
            Q{selectedQuarter}
            <QuarterChevronIcon>
              <ChevronDownIcon />
            </QuarterChevronIcon>
          </QuarterChip>
        </Dropdown>
      </TopRow>
    </HeaderControls>
  );
  
  // Show empty state when API returned no data (quarterData is null after loading completed)
  // This typically means the Process Owner hasn't been assigned to any processes yet
  // Still show header controls so user can change process owner or quarter
  if (!quarterData) {
    return (
      <Container>
        {renderHeaderControls()}
        <EmptyStateWrapper>
          <EmptyStateText>
            {isAdmin 
              ? "This process owner hasn't been assigned to any processes yet. You must assign the process owner to a process before you can see the certification details."
              : "You haven't been assigned to any processes yet. Please contact your administrator."
            }
          </EmptyStateText>
        </EmptyStateWrapper>
      </Container>
    );
  }
  
  return (
    <Container>
      {/* Header Controls */}
      {renderHeaderControls()}
      
      {/* Attestation Blurb */}
      {quarterData && (
        <AttestationBlurb>
          <AttestationHeader>
            <CheckIcon>
              <CheckCircleFilled />
            </CheckIcon>
            <AttestationContent>
              <AttestationTitle
                $editable={false}
              >
                Process Owner Acknowledgement
              </AttestationTitle>
              <AttestationDescription
                $editable={isAdmin}
                contentEditable={isAdmin}
                onBlur={(e) => isAdmin && handleAttestationSave('processOwnerMessage', e.currentTarget.textContent || '')}
                suppressContentEditableWarning
              >
                {quarterData.attestation.processOwnerMessage}
              </AttestationDescription>
            </AttestationContent>
          </AttestationHeader>
        </AttestationBlurb>
      )}
      
      {/* Review Cards */}
      {quarterData && (
        <>
          {/* 1. Objectives & Risk Assessment (RA) - Content: Process chips with links */}
          {renderReviewCard(
            quarterData.attestation.riskAssessmentTitle,
            quarterData.attestation.riskAssessmentDescription,
            'riskAssessment',
            <ContentChips>
              {allProcesses.map(p => (
                <ContentChip
                  key={p.processId}
                  onClick={() => handleProcessClick(p, true)}
                >
                  {/* <LinkOutlined className="link-icon" /> */}
                  {p.processCode} {p.processName}
                </ContentChip>
              ))}
            </ContentChips>,
            'riskAssessmentTitle',
            'riskAssessmentDescription'
          )}
          
          {/* 2. Flowcharts (FC) - Content: Flowchart file chips from all processes */}
          {renderReviewCard(
            quarterData.attestation.flowchartsTitle,
            quarterData.attestation.flowchartsDescription,
            'flowcharts',
            (() => {
              const allFlowcharts = allProcesses.flatMap(p => 
                p.flowcharts.map(f => ({ ...f, processCode: p.processCode, processName: p.processName }))
              );
              
              if (allFlowcharts.length === 0) {
                return <span style={{ color: '#9CA3AF', fontSize: 14 }}>No flowcharts available</span>;
              }
              
              return (
                <ContentChips>
                  {allFlowcharts.map((f, idx) => (
                    <ContentChipWithDownload
                      key={`${f.fileId}-${idx}`}
                      fileId={f.fileId}
                      fileName={f.fileName || `${f.processCode} Flowchart`}
                    />
                  ))}
                </ContentChips>
              );
            })(),
            'flowchartsTitle',
            'flowchartsDescription'
          )}
          
          {/* 3. Control Activities - Content: Controls grouped by process, links to EX5 */}
          {renderReviewCard(
            'Control Activities',
            '',
            'walkthroughs',
            (() => {
              const processesWithControls = allProcesses.filter(p => p.controls.length > 0);
              
              if (processesWithControls.length === 0) {
                return <span style={{ color: '#9CA3AF', fontSize: 14 }}>No control activities available</span>;
              }
              
              return (
                <ContentChips style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                  {processesWithControls.map((p) => (
                    <ControlGroupTag key={p.processId}>
                      <ControlGroupLabel>{p.processCode} {p.processName}</ControlGroupLabel>
                      {p.controls.map((c) => (
                        <ControlNumberCircle
                          key={c.controlId}
                          onClick={() => navigate(`/control-activities/${c.controlId}`)}
                        >
                          {c.controlCode.split('.').pop()}
                        </ControlNumberCircle>
                      ))}
                    </ControlGroupTag>
                  ))}
                  <ActionLink style={{ marginLeft: 8 }} onClick={() => navigate('/control-activities', {
                    state: {
                      processOwnerId: selectedProcessOwner?.id,
                      processOwnerName: selectedProcessOwner?.processOwnerName,
                    }
                  })}>View all <RightOutlined />
                  </ActionLink>
                </ContentChips>
              );
            })()
          )}
          
          {/* 4. Test Sheet (TS) - Content: Test sheet chips from all processes */}
          {renderReviewCard(
            quarterData.attestation.testSheetTitle,
            quarterData.attestation.testSheetDescription,
            'testSheet',
            (() => {
              const allTestSheets = allProcesses
                .filter(p => p.testSheetId && p.testSheetName)
                .map(p => ({ testSheetId: p.testSheetId!, testSheetName: p.testSheetName, processCode: p.processCode }));
              
              if (allTestSheets.length === 0) {
                return <span style={{ color: '#9CA3AF', fontSize: 14 }}>No test sheets available</span>;
              }
              
              return (
                <ContentChips>
                  {allTestSheets.map((ts, idx) => (
                    <ContentChipWithDownload
                      key={`${ts.testSheetId}-${idx}`}
                      fileId={ts.testSheetId}
                      fileName={ts.testSheetName || `${ts.processCode} Test Sheet`}
                    />
                  ))}
                </ContentChips>
              );
            })(),
            'testSheetTitle',
            'testSheetDescription'
          )}
          
          {/* 5. GAPs - Content: Gap chips from all processes (Red style) */}
          {renderReviewCard(
            quarterData.attestation.gapsTitle,
            quarterData.attestation.gapsDescription,
            'gaps',
            (() => {
              const allGaps = allProcesses.flatMap(p => 
                p.gaps.map(g => ({ ...g, processCode: p.processCode, processName: p.processName }))
              );
              
              if (allGaps.length === 0) {
                return <span style={{ color: '#9CA3AF', fontSize: 14 }}>No gaps identified</span>;
              }
              
              return (
                <ContentChips>
                  {allGaps.map((g, idx) => (
                    <GapChip 
                      key={`${g.gapId}-${idx}`}
                      onClick={() => navigate(`/gaps/${g.gapId}`)}
                    >
                      {g.gapCode}
                    </GapChip>
                  ))}
                </ContentChips>
              );
            })(),
            'gapsTitle',
            'gapsDescription'
          )}
          
          {/* 6. Change Indicators Checklist - Content: Link button */}
          {renderReviewCard(
            quarterData.attestation.checklistTitle,
            quarterData.attestation.checklistDescription,
            'checklist',
            <ActionLink onClick={handleGoToChecklist}>
              Go to Indicators Checklist <RightOutlined />
            </ActionLink>,
            'checklistTitle',
            'checklistDescription'
          )}
        </>
      )}
      
      {/* Acknowledge Button (PO only) */}
      {!isAdmin && (
        <FooterActions>
          <Tooltip
            title={!isChecklistCompleted ? "Please complete and submit the Change Indicators Checklist to enable certification." : ""}
            placement="top"
          >
            <span style={{ display: 'inline-block' }}>
              <AcknowledgeButton
                type="primary"
                disabled={!isChecklistCompleted}
                onClick={() => setCertifyModalVisible(true)}
              >
                <FileBadgeIcon />
                Acknowledge Certification
              </AcknowledgeButton>
            </span>
          </Tooltip>
        </FooterActions>
      )}
      
      {/* Certification Modal (PO only) */}
      <CertificationModal
        title="Are you sure you want to submit your certification?"
        open={certifyModalVisible}
        onCancel={() => {
          setCertifyModalVisible(false);
          setSelectedProcessIds([]);
        }}
        width={640}
        footer={[
          <Button key="cancel" onClick={() => setCertifyModalVisible(false)}>
            Go back
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={submitting}
            disabled={selectedProcessIds.length === 0}
            onClick={handleSubmitCertification}
          >
            Submit certification
          </Button>,
        ]}
      >
        <ModalDescription>
          By submitting, you confirm that all responses are accurate and complete.
        </ModalDescription>
        
        <ModalWarning>
          <ExclamationCircleOutlined className="icon" />
          <span className="text">
            You will not be able to edit your certification after submission.
          </span>
        </ModalWarning>
        
        <ProcessList>
          {allProcesses.length > 1 && (
            <ProcessItem>
              <Checkbox
                checked={selectedProcessIds.length === allProcesses.length}
                indeterminate={selectedProcessIds.length > 0 && selectedProcessIds.length < allProcesses.length}
                onChange={toggleSelectAll}
              />
              <ProcessItemText style={{ fontWeight: 600 }}>Select All</ProcessItemText>
            </ProcessItem>
          )}
          {allProcesses.map(p => {
            const isChecked = selectedProcessIds.includes(p.processId);
            return (
              <ProcessItem key={p.processId}>
                <Checkbox
                  checked={isChecked}
                  onChange={() => toggleProcessSelection(p.processId)}
                />
                <ProcessItemText>
                  {p.processCode} {p.processName}
                  {p.isCertified && <span style={{ color: '#9CA3AF', marginLeft: 8 }}>(Already certified)</span>}
                </ProcessItemText>
              </ProcessItem>
            );
          })}
        </ProcessList>
      </CertificationModal>
      
      {/* Comment Drawer (PO only) */}
      {!isAdmin && (
        <CommentDrawer
          open={commentDrawerOpen}
          commentType={commentType}
          onClose={() => {
            setCommentDrawerOpen(false);
            setCommentType(null);
          }}
          onSubmit={handleCommentSubmit}
        />
      )}
    </Container>
  );
};

export default CertificationDetailTab;
